import * as React from "react"
import { useState } from "react"
import { type LucideIcon } from "lucide-react"
import { useLocation, useNavigate } from "react-router-dom"
import { handleNavigationWithPaywall } from "@/utils/paywall-navigation-utils"

import { isMenuItemActive } from "../../utils/sidebar-utils"
import { HelpSidebar } from "@/app/components/onboarding/HelpSidebar"
import { useHelpContent } from "@/hooks/useHelpContent"

import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "../../components/ui/sidebar"

interface NavSecondaryItem {
  title: string
  url: string
  icon: LucideIcon | ((props: any) => React.ReactElement)
  component?: boolean
  renderComponent?: (item: any) => React.ReactNode
  isHelpSidebar?: boolean
}

function NavSecondary({
  items,
  ...props
}: {
  items: NavSecondaryItem[]
} & React.ComponentPropsWithoutRef<typeof SidebarGroup>) {
  const location = useLocation();
  const navigate = useNavigate();
  const currentPath = location.pathname.substring(1);
  const [helpSidebarOpen, setHelpSidebarOpen] = useState(false);
  const { data: helpContent } = useHelpContent();

  return (
    <>
      <SidebarGroup {...props}>
        <SidebarGroupContent>
          <SidebarMenu>
            {items.map((item) => (
              <SidebarMenuItem key={item.title}>
                {item.isHelpSidebar ? (
                  // Render Help button that opens HelpSidebar
                  <SidebarMenuButton 
                    size="sm" 
                    onClick={() => setHelpSidebarOpen(true)}
                    className="cursor-pointer"
                  >
                    <item.icon data-sentry-unmask />
                    <span data-sentry-unmask>{item.title}</span>
                  </SidebarMenuButton>
                ) : item.component && item.renderComponent ? (
                  // Render custom component if component is true and renderComponent is provided
                  item.renderComponent(item)
                ) : (
                  // Default behavior for regular links
                  <SidebarMenuButton asChild size="sm" isActive={isMenuItemActive(item.url, currentPath)}>
                    <a href={item.url} target={item.url.startsWith('#') ? undefined : "_blank"} onClick={(e) => {
                      handleNavigationWithPaywall(item.url, navigate, e);
                    }}
                    data-sentry-unmask>
                      <item.icon data-sentry-unmask />
                      <span data-sentry-unmask>{item.title}</span>
                    </a>
                  </SidebarMenuButton>
                )}
              </SidebarMenuItem>
            ))}
          </SidebarMenu>
        </SidebarGroupContent>
      </SidebarGroup>
      
      <HelpSidebar 
        isOpen={helpSidebarOpen} 
        onClose={() => setHelpSidebarOpen(false)}
        faqs={helpContent?.faqs}
        videoUrl={helpContent?.videoUrl}
        videoTitle={helpContent?.videoTitle}
      />
    </>
  )
}

export { NavSecondary }
